import '../App.css';
import {
    Box, Button,
    createTheme, CssBaseline, ThemeProvider,
    Stack, Typography, useMediaQuery,
    Chip,
} from "@mui/material";
import {Add, List as ListIcon, QrCode2, BarChart, TrendingUp} from "@mui/icons-material";
import {useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import SideBar from "../Componant/sidebar/Sidebar";
import OptionSection from "../Componant/QrCode/OptionSection";
import {listQrCodes} from "../Utils/qrcodesApi";

function StatTile({label, value, icon}) {
    return (
        <Box sx={{
            border: '1px solid', borderColor: 'divider', borderRadius: 2,
            p: 2.5, flex: '1 1 180px', minWidth: 160,
        }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 1, color: 'text.secondary'}}>
                {icon}
                <Typography variant="body2">{label}</Typography>
            </Stack>
            <Typography variant="h4" fontWeight={700}>{value}</Typography>
        </Box>
    );
}

function DashboardPage() {
    const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
    const isComputer = useMediaQuery('(min-width:900px)');
    const [sidebarIsOpen, setSidebarIsOpen] = useState();
    const navigate = useNavigate();

    const [qrcodes, setQrcodes] = useState([]);
    const [loading, setLoading] = useState(true);

    const darkTheme = createTheme({
        palette: {
            mode: prefersDarkMode ? 'dark' : 'light',
            background: {default: prefersDarkMode ? '#1A1A1A' : '#f5f5f5'},
            primary: {main: '#FF7233', light: '#FF7233', dark: '#e05a1a'},
        },
        typography: {
            fontFamily: '"Poppins", sans-serif',
            allVariants: {color: prefersDarkMode ? '#cecece' : '#000000FF'},
        },
    });

    useEffect(() => {
        listQrCodes()
            .then(setQrcodes)
            .catch((error) => console.error('Erreur lors du chargement des QR codes:', error))
            .finally(() => setLoading(false));
    }, []);

    const stats = useMemo(() => {
        const tracked = qrcodes.filter((q) => q.trackingEnabled);
        const totalScans = tracked.reduce((sum, q) => sum + (q.scanCount || 0), 0);
        const active = qrcodes.filter((q) => q.isActive).length;
        const top = [...tracked].sort((a, b) => (b.scanCount || 0) - (a.scanCount || 0)).slice(0, 5);
        return {total: qrcodes.length, trackedCount: tracked.length, totalScans, active, top};
    }, [qrcodes]);

    const getContainerClass = () => {
        const classes = [];
        if (!isComputer) {
            classes.push("mobile");
        } else {
            classes.push(sidebarIsOpen ? 'activated' : 'disactivated');
        }
        return classes.join(' ');
    };

    return (
        <div className="App">
            <ThemeProvider theme={darkTheme}>
                <SideBar sidebarIsOpen={sidebarIsOpen} setSidebarIsOpen={setSidebarIsOpen} isComputer={isComputer}/>
                <CssBaseline/>

                <Box paddingTop={'5%'} paddingBottom={'5%'} id='Md-Container' className={getContainerClass()}>
                    <Box sx={{padding: {xs: 2, sm: 4}, margin: '0 auto', width: '100%', maxWidth: 1400}}>
                        <Stack direction={{xs: 'column', sm: 'row'}} spacing={2} justifyContent="space-between" alignItems={{xs: 'stretch', sm: 'center'}} mb={4}>
                            <Typography variant="h4">Tableau de bord</Typography>
                            <Button variant="contained" color="primary" startIcon={<Add/>} onClick={() => navigate('/create')}>
                                Créer un QR code
                            </Button>
                        </Stack>

                        <Box sx={{px: {xs: 0, sm: 2}}}>
                            {loading ? (
                                <Typography>Chargement...</Typography>
                            ) : (
                                <>
                                    <Stack direction="row" flexWrap="wrap" useFlexGap spacing={2} sx={{mb: 4}}>
                                        <StatTile label="QR codes" value={stats.total} icon={<QrCode2 fontSize="small"/>}/>
                                        <StatTile label="Trackés" value={stats.trackedCount} icon={<BarChart fontSize="small"/>}/>
                                        <StatTile label="Scans totaux" value={stats.totalScans} icon={<TrendingUp fontSize="small"/>}/>
                                        <StatTile label="Actifs" value={stats.active} icon={<QrCode2 fontSize="small"/>}/>
                                    </Stack>

                                    {stats.total === 0 ? (
                                        <Typography color="text.secondary">
                                            Aucun QR code pour le moment. Créez-en un pour commencer à suivre vos scans.
                                        </Typography>
                                    ) : (
                                        <OptionSection title="QR codes les plus scannés">
                                            {stats.top.length === 0 ? (
                                                <Typography variant="body2" color="text.secondary">
                                                    Aucun QR code tracké pour le moment.
                                                </Typography>
                                            ) : (
                                                <Stack spacing={1.5}>
                                                    {stats.top.map((qrcode) => (
                                                        <Stack
                                                            key={qrcode.id} direction="row" spacing={2}
                                                            alignItems="center" justifyContent="space-between"
                                                            sx={{
                                                                cursor: 'pointer', p: 1, borderRadius: 1,
                                                                '&:hover': {bgcolor: 'action.hover'},
                                                            }}
                                                            onClick={() => navigate(`/my-qrcodes/${qrcode.id}`)}
                                                        >
                                                            <Stack direction="row" spacing={1.5} alignItems="center" sx={{minWidth: 0}}>
                                                                <Typography sx={{wordBreak: 'break-word'}}>{qrcode.name}</Typography>
                                                                {!qrcode.isActive && <Chip size="small" label="En pause" color="warning" variant="outlined"/>}
                                                            </Stack>
                                                            <Typography variant="body2" color="text.secondary" sx={{flexShrink: 0}}>
                                                                {qrcode.scanCount || 0} scans
                                                            </Typography>
                                                        </Stack>
                                                    ))}
                                                </Stack>
                                            )}
                                        </OptionSection>
                                    )}

                                    <Stack direction="row" spacing={2} sx={{mt: 4}}>
                                        <Button variant="outlined" startIcon={<ListIcon/>} onClick={() => navigate('/my-qrcodes')}>
                                            Voir tous mes QR codes
                                        </Button>
                                    </Stack>
                                </>
                            )}
                        </Box>
                    </Box>
                </Box>
            </ThemeProvider>
        </div>
    );
}

export default DashboardPage;
